import React, { useEffect, useMemo, useState } from "react";
import api from "../api/axios";

import PatientWizard from "../components/PatientWizard";
import SessionPage from "./SessionPage";
import "./Dashboard.responsive.css";

function normalizeSession(session, patientName = "Patient") {
  return {
    id: session.id,
    patient_id: session.patient_id,
    title: session.title || "Clinical Session",
    created_at: session.created_at || null,
    started_at: session.started_at || null,
    owner: patientName,
    severity: session.severity || "stable",
    status: session.status || "draft",
    summary: session.summary || "No summary available yet.",
    tags: session.tags || [],
    raw: session,
  };
}

function initials(name = "P") {
  return String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function formatDate(value) {
  if (!value) return "No date";

  try {
    return new Date(value).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "No date";
  }
}

function percent(part, total) {
  const safeTotal = Number(total || 0);
  const safePart = Number(part || 0);

  if (!safeTotal) return 0;

  return Math.max(0, Math.min(100, Math.round((safePart / safeTotal) * 100)));
}

function CircleStat({ value, label, color = "#1f7bc1", helper }) {
  const safeValue = Math.max(0, Math.min(Number(value) || 0, 100));

  return (
    <div className="mgdash-circle-stat" title={helper || label}>
      <div
        className="mgdash-circle"
        style={{
          "--circle-value": `${safeValue}%`,
          "--circle-color": color,
        }}
      >
        <span>{safeValue}%</span>
      </div>
      <small>{label}</small>
    </div>
  );
}

function ActivityGraph({ stats }) {
  const maxValue = Math.max(
    stats.totalPatients,
    stats.relatedProfiles,
    stats.totalSessions,
    stats.reviewedSessions,
    1
  );

  const bars = [
    {
      label: "Patients",
      value: stats.totalPatients,
      color: "linear-gradient(180deg, #18b7ff, #1f7bc1)",
    },
    {
      label: "Linked",
      value: stats.relatedProfiles,
      color: "linear-gradient(180deg, #62d7ff, #2d8fe0)",
    },
    {
      label: "Sessions",
      value: stats.totalSessions,
      color: "linear-gradient(180deg, #94d8ff, #1769a9)",
    },
    {
      label: "Reviewed",
      value: stats.reviewedSessions,
      color: "linear-gradient(180deg, #fbbf24, #f59e0b)",
    },
  ];

  return (
    <div className="mgdash-activity-card">
      <div className="mgdash-activity-head">
        <div>
          <span>Clinical Activity</span>
          <strong>Workspace signal</strong>
        </div>
        <em>Live</em>
      </div>

      <div className="mgdash-bar-chart">
        {bars.map((bar) => {
          const height = Math.max(18, Math.round((bar.value / maxValue) * 100));

          return (
            <div className="mgdash-bar-item" key={bar.label}>
              <div className="mgdash-bar-track">
                <i
                  style={{
                    height: `${height}%`,
                    background: bar.color,
                  }}
                />
              </div>
              <b>{bar.value}</b>
              <small>{bar.label}</small>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ClinicalSignalExplanation({ stats }) {
  return (
    <div className="mgdash-signal-explain">
      <div>
        <span>Intake</span>
        <strong>
          {stats.patientsWithSessions}/{stats.totalPatients}
        </strong>
        <p>Patients that have at least one clinical session.</p>
      </div>

      <div>
        <span>Review</span>
        <strong>
          {stats.reviewedSessions}/{stats.totalSessions}
        </strong>
        <p>Sessions marked reviewed or completed.</p>
      </div>

      <div>
        <span>Risk</span>
        <strong>
          {stats.highPriority}/{stats.totalSessions}
        </strong>
        <p>Sessions marked high or critical priority.</p>
      </div>
    </div>
  );
}

function WorkspaceInsightPanel({ stats, selectedPatient, loadingWorkspaceStats }) {
  const sessionHealth =
    stats.totalSessions === 0
      ? "No clinical sessions yet"
      : stats.riskPercent >= 50
      ? "High attention required"
      : stats.reviewPercent >= 70
      ? "Review workflow healthy"
      : "Clinical workflow in progress";

  const patientReadiness =
    stats.totalPatients === 0
      ? "Create first patient"
      : stats.intakePercent >= 80
      ? "Most profiles have intake"
      : "Some profiles still need sessions";

  return (
    <div className="mgdash-insight-grid">
      <div className="mgdash-insight-card wide">
        <div>
          <span>Workspace Intelligence</span>
          <strong>{sessionHealth}</strong>
          <p>
            Dashboard signals are calculated from real patient and session data:
            intake coverage, review completion, and risk load.
          </p>
        </div>

        <div className="mgdash-pulse-line">
          <i />
        </div>
      </div>

      <div className="mgdash-insight-card">
        <span>Patient Readiness</span>
        <strong>{patientReadiness}</strong>
        <p>
          {stats.patientsWithSessions}/{stats.totalPatients} patients have at
          least one clinical session.
        </p>
      </div>

      <div className="mgdash-insight-card">
        <span>Selected Patient</span>
        <strong>{selectedPatient?.name || "No patient selected"}</strong>
        <p>
          {selectedPatient
            ? "Ready for intake, analysis, review, and clinical mapping."
            : "Select a patient to unlock session timeline."}
        </p>
      </div>

      <div className="mgdash-insight-card">
        <span>Sync State</span>
        <strong>{loadingWorkspaceStats ? "Refreshing" : "Live"}</strong>
        <p>Workspace counters refresh after patient/session changes.</p>
      </div>
    </div>
  );
}

export default function Dashboard({ user }) {
  const [patients, setPatients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [patientSessionCounts, setPatientSessionCounts] = useState({});

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const [showWizard, setShowWizard] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);

  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingWorkspaceStats, setLoadingWorkspaceStats] = useState(false);

  const [patientSearch, setPatientSearch] = useState("");
  const [relationFilter, setRelationFilter] = useState("all");
  const [sessionSearch, setSessionSearch] = useState("");
  const [sessionStatusFilter, setSessionStatusFilter] = useState("all");

  const [showAllPatients, setShowAllPatients] = useState(false);
  const [showAllSessions, setShowAllSessions] = useState(false);

  useEffect(() => {
    loadPatients();
  }, []);

  const handleAuthFailure = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_id");
    window.location.href = "/login";
  };

  const getErrorMessage = (e, fallback) => {
    return e?.response?.data?.detail || fallback;
  };

  const loadPatients = async () => {
    try {
      setLoadingPatients(true);

      const res = await api.get("/api/patient/");
      const patientList = res.data || [];

      setPatients(patientList);
      await loadWorkspaceSessionStats(patientList);
    } catch (e) {
      if (e?.response?.status === 401) {
        handleAuthFailure();
        return;
      }

      alert(getErrorMessage(e, "Could not load patients"));
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadWorkspaceSessionStats = async (patientList) => {
    try {
      setLoadingWorkspaceStats(true);

      const results = await Promise.all(
        (patientList || []).map(async (patient) => {
          try {
            const res = await api.get(`/api/session/patient/${patient.id}`);

            const normalized = (res.data || []).map((session) =>
              normalizeSession(session, patient?.name || "Patient")
            );

            return {
              patientId: patient.id,
              sessions: normalized,
            };
          } catch {
            return {
              patientId: patient.id,
              sessions: [],
            };
          }
        })
      );

      const nextCounts = {};
      const nextSessions = [];

      results.forEach((item) => {
        nextCounts[item.patientId] = item.sessions.length;
        nextSessions.push(...item.sessions);
      });

      setPatientSessionCounts(nextCounts);
      setAllSessions(nextSessions);

      if (selectedPatient) {
        const latestSelected = results.find(
          (item) => String(item.patientId) === String(selectedPatient.id)
        );

        if (latestSelected) {
          setSessions(latestSelected.sessions);
        }
      }
    } finally {
      setLoadingWorkspaceStats(false);
    }
  };

  const loadSessions = async (patient) => {
    try {
      setLoadingSessions(true);

      const res = await api.get(`/api/session/patient/${patient.id}`);
      const normalized = (res.data || []).map((session) =>
        normalizeSession(session, patient?.name || "Patient")
      );

      setSessions(normalized);

      setPatientSessionCounts((prev) => ({
        ...prev,
        [patient.id]: normalized.length,
      }));

      setAllSessions((prev) => {
        const withoutCurrentPatient = prev.filter(
          (session) => String(session.patient_id) !== String(patient.id)
        );

        return [...withoutCurrentPatient, ...normalized];
      });
    } catch (e) {
      if (e?.response?.status === 401) {
        handleAuthFailure();
        return;
      }

      alert(getErrorMessage(e, "Could not load sessions"));
    } finally {
      setLoadingSessions(false);
    }
  };

  const handlePatientSelect = async (patient) => {
    setSelectedPatient(patient);
    setSelectedSessionId(null);
    await loadSessions(patient);
  };

  const createSession = async () => {
    if (!selectedPatient) {
      alert("Please select a patient first.");
      return;
    }

    try {
      const res = await api.post("/api/session/", {
        patient_id: selectedPatient.id,
        title: "Clinical Session",
        modality: "text",
      });

      await loadSessions(selectedPatient);
      await loadWorkspaceSessionStats(patients);

      setSelectedSessionId(res.data.id);
    } catch (e) {
      const message = getErrorMessage(
        e,
        "Could not create session. Please try again."
      );

      if (e?.response?.status === 401) {
        handleAuthFailure();
        return;
      }

      if (e?.response?.status === 403) {
        alert(message);
        return;
      }

      alert(message);
    }
  };

  const handleDeletePatient = async (patient) => {
    const confirmed = window.confirm(
      `Delete patient "${patient.name}" and all linked sessions?`
    );

    if (!confirmed) return;

    try {
      await api.delete(`/api/patient/${patient.id}`);

      const nextPatients = patients.filter((p) => p.id !== patient.id);

      setPatients(nextPatients);
      setAllSessions((prev) =>
        prev.filter((session) => String(session.patient_id) !== String(patient.id))
      );

      setPatientSessionCounts((prev) => {
        const copy = { ...prev };
        delete copy[patient.id];
        return copy;
      });

      if (selectedPatient?.id === patient.id) {
        setSelectedPatient(null);
        setSessions([]);
        setSelectedSessionId(null);
      }

      await loadWorkspaceSessionStats(nextPatients);
    } catch (e) {
      if (e?.response?.status === 401) {
        handleAuthFailure();
        return;
      }

      alert(getErrorMessage(e, "Could not delete patient"));
    }
  };

  const filteredPatients = useMemo(() => {
    return patients.filter((patient) => {
      const search = patientSearch.toLowerCase().trim();
      const relation = (patient.relation || "self").toLowerCase();

      const matchesSearch =
        !search ||
        patient.name?.toLowerCase().includes(search) ||
        patient.gender?.toLowerCase().includes(search) ||
        relation.includes(search);

      const matchesRelation =
        relationFilter === "all" || relation === relationFilter.toLowerCase();

      return matchesSearch && matchesRelation;
    });
  }, [patients, patientSearch, relationFilter]);

  const visiblePatients = useMemo(() => {
    return showAllPatients ? filteredPatients : filteredPatients.slice(0, 6);
  }, [filteredPatients, showAllPatients]);

  const filteredSessions = useMemo(() => {
    return sessions.filter((session) => {
      const haystack = [
        session.title,
        session.summary,
        session.status,
        session.severity,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !sessionSearch.trim() || haystack.includes(sessionSearch.toLowerCase());

      const matchesStatus =
        sessionStatusFilter === "all" ||
        (session.status || "").toLowerCase() ===
          sessionStatusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [sessions, sessionSearch, sessionStatusFilter]);

  const visibleSessions = useMemo(() => {
    return showAllSessions ? filteredSessions : filteredSessions.slice(0, 4);
  }, [filteredSessions, showAllSessions]);

  const stats = useMemo(() => {
    const relatedProfiles = patients.filter(
      (p) => (p.relation || "self").toLowerCase() !== "self"
    ).length;

    const reviewedSessions = allSessions.filter((s) =>
      ["reviewed", "completed"].includes((s.status || "").toLowerCase())
    ).length;

    const highPriority = allSessions.filter((s) =>
      ["high", "critical"].includes((s.severity || "").toLowerCase())
    ).length;

    const patientsWithSessions = patients.filter(
      (patient) => Number(patientSessionCounts[patient.id] || 0) > 0
    ).length;

    return {
      totalPatients: patients.length,
      totalSessions: allSessions.length,
      selectedPatientSessions: sessions.length,
      relatedProfiles,
      reviewedSessions,
      highPriority,
      patientsWithSessions,
      activePatient: selectedPatient ? selectedPatient.name : "No patient selected",
      intakePercent: percent(patientsWithSessions, patients.length),
      reviewPercent: percent(reviewedSessions, allSessions.length),
      riskPercent: percent(highPriority, allSessions.length),
    };
  }, [patients, allSessions, sessions, selectedPatient, patientSessionCounts]);

  if (selectedSessionId) {
    return (
      <div className="mgdash-page">
        <style>{dashboardCss}</style>

        <SessionPage
          sessionId={selectedSessionId}
          onBack={() => setSelectedSessionId(null)}
          user={user}
        />
      </div>
    );
  }

  return (
    <div className="mgdash-page">
      <style>{dashboardCss}</style>

      <div className="mgdash-shell">
        <aside className="mgdash-rail">
          <section className="mgdash-steps-card">
            <div className="mgdash-kicker muted-blue">Clinical Intelligence</div>

            <div className="mgdash-step active">
              <b>1.</b>
              <span>Medical Intelligence Platform</span>
              <small>Patient management</small>
            </div>

            <div className="mgdash-step">
              <b>2.</b>
              <span>Interactive clinical graph</span>
              <small>Analysis and impact mapping</small>
            </div>

            <div className="mgdash-step">
              <b>3.</b>
              <span>Pharmacology simulation lab</span>
              <small>Chemical impact workflow</small>
            </div>

            <div className="mgdash-step">
              <b>4.</b>
              <span>Brain impact mapping</span>
              <small>Neuron-style analysis</small>
            </div>
          </section>

          <section className="mgdash-card mgdash-action-card">
            <div className="mgdash-kicker">Quick Actions</div>

            <button
              className="mgdash-btn primary"
              onClick={() => {
                setEditingPatient(null);
                setShowWizard(true);
              }}
              type="button"
            >
              Add Patient
            </button>

            <button
              className="mgdash-btn soft"
              onClick={createSession}
              disabled={!selectedPatient}
              type="button"
            >
              New Session
            </button>

            <button
              className="mgdash-btn ghost"
              onClick={loadPatients}
              disabled={loadingPatients || loadingWorkspaceStats}
              type="button"
            >
              {loadingPatients || loadingWorkspaceStats
                ? "Refreshing..."
                : "Refresh Workspace"}
            </button>
          </section>

          <section className="mgdash-card mgdash-selected-card">
            <div className="mgdash-kicker">Selected Profile</div>

            <h3>{selectedPatient ? selectedPatient.name : "No patient selected"}</h3>

            <p>
              {selectedPatient
                ? `${selectedPatient.age ?? "-"} yrs • ${
                    selectedPatient.gender || "-"
                  } • ${selectedPatient.relation || "self"}`
                : "Pick a patient to review sessions, family links, and clinical summaries."}
            </p>

            <div className="mgdash-pill-row">
              <span>Privacy enabled</span>
              <span>Graph ready</span>
            </div>
          </section>
        </aside>

        <main className="mgdash-main">
          <section className="mgdash-hero-card">
            <div className="mgdash-hero-copy">
              <div className="mgdash-kicker blue">MedGen Workspace</div>

              <h1>Medical Intelligence Platform</h1>

              <p>
                A premium patient intelligence cockpit for intake, linked
                profiles, secure sessions, review status, and multimodal
                reasoning.
              </p>

              <div className="mgdash-hero-actions">
                <button
                  className="mgdash-btn primary"
                  onClick={() => {
                    setEditingPatient(null);
                    setShowWizard(true);
                  }}
                  type="button"
                >
                  + Add Patient
                </button>

                <button
                  className="mgdash-btn soft"
                  onClick={createSession}
                  disabled={!selectedPatient}
                  type="button"
                >
                  Start Session
                </button>
              </div>

              <ActivityGraph stats={stats} />

              <WorkspaceInsightPanel
                stats={stats}
                selectedPatient={selectedPatient}
                loadingWorkspaceStats={loadingWorkspaceStats}
              />
            </div>

            <div className="mgdash-cockpit">
              <div className="mgdash-cockpit-top">
                <div>
                  <div className="mgdash-kicker muted-blue">System Status</div>
                  <h3>Clinical mode active</h3>
                </div>

                <span>{loadingWorkspaceStats ? "Syncing" : "Live"}</span>
              </div>

              <p>
                Cockpit signals are calculated from real workspace data. No
                hardcoded numbers. Real intake, review and risk signals.
              </p>

              <div className="mgdash-circle-row">
                <CircleStat
                  value={stats.intakePercent}
                  label="Intake"
                  color="#1f7bc1"
                  helper="Patients with at least one clinical session"
                />

                <CircleStat
                  value={stats.reviewPercent}
                  label="Review"
                  color="#f59e0b"
                  helper="Reviewed or completed sessions"
                />

                <CircleStat
                  value={stats.riskPercent}
                  label="Risk"
                  color="#18b7ff"
                  helper="High or critical sessions"
                />
              </div>

              <ClinicalSignalExplanation stats={stats} />

              <div className="mgdash-cockpit-boxes">
                <div>
                  <span>Selected profile</span>
                  <strong>{stats.activePatient}</strong>
                </div>

                <div>
                  <span>Total sessions</span>
                  <strong>{stats.totalSessions}</strong>
                </div>
              </div>
            </div>
          </section>

          <section className="mgdash-section">
            <div className="mgdash-section-head">
              <div>
                <div className="mgdash-kicker blue">Patient Directory</div>

                <h2>Patients</h2>

                <p>
                  Choose a profile to continue intake or start a new medical
                  session.
                </p>
              </div>

              <div className="mgdash-filters">
                <input
                  placeholder="Search patients"
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                />

                <select
                  value={relationFilter}
                  onChange={(e) => setRelationFilter(e.target.value)}
                >
                  <option value="all">All relations</option>
                  <option value="self">Self</option>
                  <option value="mother">Mother</option>
                  <option value="father">Father</option>
                  <option value="spouse">Spouse</option>
                  <option value="child">Child</option>
                  <option value="sibling">Sibling</option>
                </select>

                <button
                  className="mgdash-btn ghost"
                  type="button"
                  onClick={() => setShowAllPatients((prev) => !prev)}
                >
                  {showAllPatients ? "Show less" : "See all"}
                </button>
              </div>
            </div>

            {loadingPatients ? (
              <div className="mgdash-empty">Loading patients...</div>
            ) : filteredPatients.length === 0 ? (
              <div className="mgdash-empty">
                <h3>No patients found</h3>
                <p>Try changing filters or create the first patient profile.</p>

                <button
                  className="mgdash-btn primary"
                  onClick={() => {
                    setEditingPatient(null);
                    setShowWizard(true);
                  }}
                  type="button"
                >
                  Create Patient
                </button>
              </div>
            ) : (
              <div className="mgdash-patient-grid">
                {visiblePatients.map((patient) => {
                  const selected = selectedPatient?.id === patient.id;
                  const sessionCount = Number(patientSessionCounts[patient.id] || 0);

                  return (
                    <article
                      key={patient.id}
                      className={
                        selected
                          ? "mgdash-patient-card selected"
                          : "mgdash-patient-card"
                      }
                      onClick={() => handlePatientSelect(patient)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handlePatientSelect(patient);
                      }}
                    >
                      <div className="mgdash-patient-top">
                        <div className="mgdash-avatar">{initials(patient.name)}</div>

                        <div className="mgdash-patient-meta">
                          <h3>{patient.name}</h3>
                          <p>
                            {patient.age ?? "-"} yrs • {patient.gender || "-"}
                          </p>
                        </div>

                        <span className="mgdash-count-chip">
                          {sessionCount} sessions
                        </span>
                      </div>

                      <div className="mgdash-patient-bottom">
                        <span className="mgdash-relation-chip">
                          {patient.relation || "self"}
                        </span>

                        <div className="mgdash-card-actions">
                          <button
                            className="mgdash-btn tiny"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingPatient(patient);
                              setShowWizard(true);
                            }}
                          >
                            Edit
                          </button>

                          <button
                            className="mgdash-btn tiny danger"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePatient(patient);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <section className="mgdash-section">
            <div className="mgdash-section-head">
              <div>
                <div className="mgdash-kicker blue">Timeline</div>

                <h2>Recent Sessions</h2>

                <p>
                  {selectedPatient
                    ? `Showing sessions for ${selectedPatient.name}`
                    : "Select a patient to view session history"}
                </p>
              </div>

              <div className="mgdash-filters session">
                <input
                  placeholder="Search sessions"
                  value={sessionSearch}
                  onChange={(e) => setSessionSearch(e.target.value)}
                  disabled={!selectedPatient}
                />

                <select
                  value={sessionStatusFilter}
                  onChange={(e) => setSessionStatusFilter(e.target.value)}
                  disabled={!selectedPatient}
                >
                  <option value="all">All statuses</option>
                  <option value="draft">Draft</option>
                  <option value="completed">Completed</option>
                  <option value="reviewed">Reviewed</option>
                </select>

                <button
                  className="mgdash-btn ghost"
                  type="button"
                  onClick={() => setShowAllSessions((prev) => !prev)}
                  disabled={!selectedPatient}
                >
                  {showAllSessions ? "Show less" : "See all"}
                </button>

                <button
                  className="mgdash-btn soft"
                  onClick={createSession}
                  disabled={!selectedPatient}
                  type="button"
                >
                  New Session
                </button>
              </div>
            </div>

            {!selectedPatient ? (
              <div className="mgdash-empty">
                <h3>No patient selected</h3>
                <p>Choose a patient card above to view and manage sessions.</p>
              </div>
            ) : loadingSessions ? (
              <div className="mgdash-empty">Loading sessions...</div>
            ) : filteredSessions.length === 0 ? (
              <div className="mgdash-empty">
                <h3>No sessions found</h3>
                <p>
                  Create a new session for {selectedPatient.name} or adjust your
                  filters.
                </p>

                <button
                  className="mgdash-btn primary"
                  onClick={createSession}
                  type="button"
                >
                  Start Session
                </button>
              </div>
            ) : (
              <div className="mgdash-session-grid">
                {visibleSessions.map((session) => (
                  <article
                    key={session.id}
                    className="mgdash-session-card"
                    onClick={() => setSelectedSessionId(session.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setSelectedSessionId(session.id);
                    }}
                  >
                    <div className="mgdash-session-top">
                      <div>
                        <h3>{session.title}</h3>
                        <p>
                          {formatDate(session.created_at || session.started_at)} •{" "}
                          {session.owner}
                        </p>
                      </div>

                      <div className="mgdash-session-badges">
                        <span>{session.status}</span>
                        <span>{session.severity}</span>
                      </div>
                    </div>

                    <p className="mgdash-session-summary">{session.summary}</p>
                  </article>
                ))}
              </div>
            )}
          </section>
        </main>
      </div>

      {showWizard && (
        <PatientWizard
          patient={editingPatient}
          onClose={() => {
            setShowWizard(false);
            setEditingPatient(null);
          }}
          onSaved={async (savedPatient) => {
            setPatients((prev) => {
              const exists = prev.some((p) => p.id === savedPatient.id);

              if (exists) {
                return prev.map((p) =>
                  p.id === savedPatient.id ? savedPatient : p
                );
              }

              return [savedPatient, ...prev];
            });

            setSelectedPatient(savedPatient);
            setShowWizard(false);
            setEditingPatient(null);

            await loadPatients();
          }}
        />
      )}
    </div>
  );
}

const dashboardCss = `
.mgdash-page {
  --mg-black: #03060c;
  --mg-navy: #06101f;
  --mg-navy-2: #081525;
  --mg-navy-3: #10243a;
  --mg-blue: #1f7bc1;
  --mg-blue-2: #2d8fe0;
  --mg-cyan: #18b7ff;
  --mg-text: #07111f;
  --mg-muted: #66778e;
  --mg-border: #cddcec;
  --mg-soft: #f5f9fd;
  --mg-shadow: 0 20px 55px rgba(7, 17, 31, 0.08);

  width: 100%;
  color: var(--mg-text);
}

.mgdash-page * {
  box-sizing: border-box;
}

.mgdash-shell {
  width: 100%;
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
  padding: 0 10px 10px;
  margin-top: 10px;
}

.mgdash-rail {
  display: grid;
  gap: 16px;
  position: sticky;
  margin-top: 0;
}

.mgdash-card,
.mgdash-steps-card,
.mgdash-empty,
.mgdash-patient-card,
.mgdash-session-card,
.mgdash-hero-card {
  border: 1px solid var(--mg-border);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 12px 35px rgba(7, 17, 31, 0.055);
}

.mgdash-steps-card {
  padding: 18px;
  background:
    radial-gradient(circle at 90% 0%, rgba(24, 183, 255, 0.17), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 65%, #06101f);
  border-color: rgba(24, 183, 255, 0.18);
}

.mgdash-kicker {
  margin: 0 0 12px;
  color: #60758e;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

.mgdash-kicker.blue {
  color: #125a94;
}

.mgdash-kicker.muted-blue {
  color: #89bfe8;
}

.mgdash-step {
  display: grid;
  grid-template-columns: 28px 1fr;
  column-gap: 8px;
  row-gap: 1px;
  align-items: start;
  min-height: 58px;
  margin-bottom: 8px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.72);
  border: 1px solid rgba(255,255,255,0.08);
}

.mgdash-step.active {
  background: rgba(255,255,255,0.95);
  color: #07111f;
}

.mgdash-step b {
  grid-row: 1 / span 2;
  font-size: 13px;
}

.mgdash-step span {
  font-size: 12px;
  font-weight: 850;
  line-height: 1.25;
}

.mgdash-step small {
  color: inherit;
  opacity: 0.72;
  font-weight: 750;
}

.mgdash-action-card,
.mgdash-selected-card {
  padding: 18px;
}

.mgdash-selected-card h3 {
  margin: 0 0 10px;
  font-size: 18px;
}

.mgdash-selected-card p {
  margin: 0 0 14px;
  color: var(--mg-muted);
  line-height: 1.55;
}

.mgdash-btn {
  min-height: 38px;
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 850;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: 0.16s ease;
  white-space: nowrap;
}

.mgdash-btn:disabled {
  opacity: 0.52;
  cursor: not-allowed;
}

.mgdash-btn.primary {
  color: white;
  background: linear-gradient(135deg, var(--mg-blue), #0f5f9d);
  box-shadow: 0 12px 26px rgba(31, 123, 193, 0.22);
}

.mgdash-btn.soft,
.mgdash-btn.ghost,
.mgdash-btn.tiny {
  color: #112238;
  background: #f8fbff;
  border-color: var(--mg-border);
}

.mgdash-btn.tiny {
  min-height: 34px;
  padding: 7px 11px;
  font-size: 12px;
}

.mgdash-btn.danger {
  color: #991b1b;
}

.mgdash-action-card .mgdash-btn {
  width: 100%;
  margin-top: 8px;
}

.mgdash-pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mgdash-pill-row span,
.mgdash-relation-chip,
.mgdash-count-chip,
.mgdash-session-badges span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 5px 9px;
  border-radius: 999px;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  color: #0b4e84;
  font-size: 11px;
  font-weight: 850;
  text-transform: capitalize;
}

.mgdash-main {
  display: grid;
  gap: 30px;
  min-width: 0;
}

.mgdash-hero-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 430px;
  gap: 32px;
  align-items: stretch;
  padding: 30px;
  min-height: 330px;
}

.mgdash-hero-copy h1 {
  margin: 0;
  max-width: 800px;
  font-size: clamp(34px, 3.2vw, 50px);
  line-height: 1.04;
  letter-spacing: -0.025em;
  font-weight: 900;
  word-spacing: 0;
}

.mgdash-hero-copy p {
  max-width: 760px;
  margin: 18px 0 18px;
  color: var(--mg-muted);
  font-size: 15px;
  line-height: 1.7;
}

.mgdash-hero-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.mgdash-activity-card {
  margin-top: 28px;
  padding: 18px;
  border-radius: 18px;
  border: 1px solid var(--mg-border);
  background:
    radial-gradient(circle at 100% 0%, rgba(31, 123, 193, 0.08), transparent 32%),
    linear-gradient(180deg, #ffffff, #f7fbff);
}

.mgdash-activity-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 16px;
}

.mgdash-activity-head span {
  display: block;
  margin-bottom: 5px;
  color: #75869d;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgdash-activity-head strong {
  display: block;
  color: #07111f;
  font-size: 17px;
}

.mgdash-activity-head em {
  padding: 5px 10px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-size: 11px;
  font-weight: 900;
  font-style: normal;
}

.mgdash-bar-chart {
  height: 165px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  align-items: end;
}

.mgdash-bar-item {
  min-width: 0;
  display: grid;
  justify-items: center;
  gap: 7px;
}

.mgdash-bar-track {
  width: 100%;
  max-width: 70px;
  height: 105px;
  display: flex;
  align-items: end;
  padding: 5px;
  border-radius: 16px;
  background: #edf4fb;
  border: 1px solid #dce8f4;
}

.mgdash-bar-track i {
  display: block;
  width: 100%;
  min-height: 18px;
  border-radius: 12px;
  box-shadow: 0 10px 20px rgba(31, 123, 193, 0.18);
  animation: mgdashBarRise 0.7s ease both;
}

.mgdash-bar-item b {
  color: #07111f;
  font-size: 18px;
  font-weight: 950;
}

.mgdash-bar-item small {
  color: var(--mg-muted);
  font-size: 11px;
  font-weight: 850;
}

.mgdash-insight-grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  gap: 12px;
}

.mgdash-insight-card {
  position: relative;
  overflow: hidden;
  min-height: 118px;
  padding: 16px;
  border-radius: 18px;
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.12), transparent 34%),
    #ffffff;
  border: 1px solid #d9e8f6;
}

.mgdash-insight-card.wide {
  grid-row: span 2;
  min-height: 248px;
  background:
    radial-gradient(circle at 88% 0%, rgba(24, 183, 255, 0.14), transparent 34%),
    linear-gradient(145deg, #ffffff, #eef7ff);
}

.mgdash-insight-card span {
  display: block;
  margin-bottom: 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.mgdash-insight-card strong {
  display: block;
  color: var(--mg-text);
  font-size: 18px;
  font-weight: 950;
  letter-spacing: -0.035em;
}

.mgdash-insight-card p {
  margin: 9px 0 0;
  color: var(--mg-muted);
  font-size: 13px;
  line-height: 1.55;
}

.mgdash-pulse-line {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 16px;
  height: 74px;
  overflow: hidden;
  border-radius: 16px;
  background:
    linear-gradient(rgba(31,123,193,0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(31,123,193,0.08) 1px, transparent 1px);
  background-size: 18px 18px;
  border: 1px solid rgba(31,123,193,0.10);
}

.mgdash-pulse-line i {
  position: absolute;
  left: -20%;
  top: 50%;
  width: 140%;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(24,183,255,0.15),
    rgba(24,183,255,0.95),
    rgba(31,123,193,0.35),
    transparent
  );
  box-shadow: 0 0 18px rgba(24,183,255,0.50);
  animation: mgdashPulseWave 2.4s linear infinite;
}

.mgdash-cockpit {
  padding: 22px;
  border-radius: 22px;
  color: white;
  background:
    radial-gradient(circle at 92% 0%, rgba(24, 183, 255, 0.20), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.18);
  box-shadow: 0 20px 55px rgba(3, 6, 12, 0.16);
}

.mgdash-cockpit-top {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 14px;
}

.mgdash-cockpit-top h3 {
  margin: 0;
  color: white;
  font-size: 23px;
  letter-spacing: -0.04em;
}

.mgdash-cockpit-top > span {
  padding: 5px 10px;
  border-radius: 999px;
  color: #bfeaff;
  background: rgba(24,183,255,0.12);
  border: 1px solid rgba(24,183,255,0.18);
  font-size: 11px;
  font-weight: 900;
}

.mgdash-cockpit p {
  margin: 18px 0 20px;
  color: #a9bdd3;
  line-height: 1.65;
}

.mgdash-circle-row {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  margin: 20px 0;
}

.mgdash-circle-stat {
  display: grid;
  justify-items: center;
  gap: 8px;
}

.mgdash-circle {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background:
    radial-gradient(circle at center, #07111f 0 55%, transparent 56%),
    conic-gradient(var(--circle-color) var(--circle-value), rgba(255,255,255,0.13) 0);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.10);
  transition: 0.3s ease;
}

.mgdash-circle span {
  font-size: 15px;
  font-weight: 950;
  color: white;
}

.mgdash-circle-stat small {
  color: #a9bdd3;
  font-size: 11px;
  font-weight: 850;
}

.mgdash-signal-explain {
  display: grid;
  gap: 10px;
  margin: 18px 0;
}

.mgdash-signal-explain div {
  padding: 13px;
  border-radius: 14px;
  background: rgba(255,255,255,0.075);
  border: 1px solid rgba(24,183,255,0.13);
}

.mgdash-signal-explain span {
  display: block;
  margin-bottom: 5px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgdash-signal-explain strong {
  display: block;
  color: white;
  font-size: 16px;
  font-weight: 950;
}

.mgdash-signal-explain p {
  margin: 4px 0 0;
  color: #a9bdd3;
  font-size: 12px;
  line-height: 1.45;
}

.mgdash-cockpit-boxes {
  display: grid;
  gap: 12px;
}

.mgdash-cockpit-boxes div {
  min-height: 70px;
  padding: 15px;
  border-radius: 14px;
  background: rgba(255,255,255,0.96);
}

.mgdash-cockpit-boxes span {
  display: block;
  margin-bottom: 6px;
  color: #75869d;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.mgdash-cockpit-boxes strong {
  color: #07111f;
  font-size: 15px;
}

.mgdash-section {
  display: grid;
  gap: 16px;
}

.mgdash-section-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 520px);
  gap: 18px;
  align-items: end;
}

.mgdash-section-head h2 {
  margin: 0 0 7px;
  font-size: 26px;
  letter-spacing: -0.045em;
}

.mgdash-section-head p {
  margin: 0;
  color: var(--mg-muted);
  line-height: 1.55;
}

.mgdash-filters {
  display: grid;
  grid-template-columns: minmax(170px, 1fr) 160px 100px;
  gap: 10px;
}

.mgdash-filters.session {
  grid-template-columns: minmax(170px, 1fr) 150px 100px 130px;
}

.mgdash-filters input,
.mgdash-filters select {
  width: 100%;
  min-height: 38px;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid var(--mg-border);
  background: white;
  color: #112238;
  font-size: 13px;
  outline: none;
}

.mgdash-patient-grid,
.mgdash-session-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(315px, 1fr));
  gap: 16px;
}

.mgdash-patient-card {
  min-height: 164px;
  padding: 18px;
  cursor: pointer;
  transition: 0.16s ease;
}

.mgdash-patient-card:hover,
.mgdash-session-card:hover {
  transform: translateY(-2px);
  border-color: rgba(31, 123, 193, 0.55);
  box-shadow: 0 0 0 1px rgba(24, 183, 255, 0.14), 0 18px 44px rgba(31, 123, 193, 0.12);
}

.mgdash-patient-card.selected {
  border-color: #1f7bc1;
  background: #f3f9ff;
  box-shadow: 0 0 0 3px rgba(31, 123, 193, 0.12);
}

.mgdash-patient-top {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: start;
}

.mgdash-avatar {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: #0b4e84;
  background: rgba(31,123,193,0.10);
  font-weight: 950;
}

.mgdash-patient-meta h3,
.mgdash-session-card h3 {
  margin: 0 0 5px;
  font-size: 18px;
  letter-spacing: -0.035em;
}

.mgdash-patient-meta p,
.mgdash-session-card p {
  margin: 0;
  color: var(--mg-muted);
}

.mgdash-patient-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 28px;
}

.mgdash-card-actions {
  display: flex;
  gap: 8px;
}

.mgdash-session-card {
  min-height: 170px;
  padding: 18px;
  cursor: pointer;
}

.mgdash-session-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.mgdash-session-badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.mgdash-session-summary {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-top: 16px !important;
  line-height: 1.55;
}

.mgdash-empty {
  padding: 22px;
}

.mgdash-empty h3 {
  margin: 0 0 8px;
  font-size: 18px;
}

.mgdash-empty p {
  margin: 0 0 14px;
  color: var(--mg-muted);
}

@keyframes mgdashBarRise {
  from {
    transform: scaleY(0.4);
    opacity: 0.45;
    transform-origin: bottom;
  }

  to {
    transform: scaleY(1);
    opacity: 1;
    transform-origin: bottom;
  }
}

@keyframes mgdashPulseWave {
  0% {
    transform: translateX(-12%) translateY(-50%) scaleX(0.85);
    opacity: 0.2;
  }

  45% {
    opacity: 1;
  }

  100% {
    transform: translateX(18%) translateY(-50%) scaleX(1);
    opacity: 0.25;
  }
}

@media (max-width: 1380px) {
  .mgdash-shell {
    grid-template-columns: 280px minmax(0, 1fr);
    padding: 0 22px 42px;
    margin-top: 22px;
  }

  .mgdash-hero-card {
    grid-template-columns: 1fr;
  }

  .mgdash-cockpit {
    max-width: 620px;
  }

  .mgdash-insight-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .mgdash-insight-card.wide {
    grid-column: 1 / -1;
    grid-row: auto;
  }
}

@media (max-width: 1080px) {
  .mgdash-shell {
    grid-template-columns: 1fr;
  }

  .mgdash-rail {
    position: static;
  }

  .mgdash-section-head {
    grid-template-columns: 1fr;
  }

  .mgdash-filters,
  .mgdash-filters.session {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 720px) {
  .mgdash-shell {
    padding: 0 12px 32px;
    margin-top: 14px;
    gap: 16px;
  }

  .mgdash-hero-card {
    padding: 18px;
  }

  .mgdash-hero-copy h1 {
    font-size: 32px;
    letter-spacing: -0.02em;
  }

  .mgdash-bar-chart {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
  }

  .mgdash-bar-track {
    max-width: 48px;
  }

  .mgdash-circle-row {
    gap: 8px;
  }

  .mgdash-circle {
    width: 72px;
    height: 72px;
  }

  .mgdash-insight-grid {
    grid-template-columns: 1fr;
  }

  .mgdash-patient-grid,
  .mgdash-session-grid {
    grid-template-columns: 1fr;
  }

  .mgdash-filters,
  .mgdash-filters.session {
    grid-template-columns: 1fr;
  }

  .mgdash-patient-top {
    grid-template-columns: 46px minmax(0, 1fr);
  }

  .mgdash-count-chip {
    grid-column: 1 / -1;
    width: fit-content;
  }

  .mgdash-patient-bottom,
  .mgdash-session-top {
    flex-direction: column;
    align-items: flex-start;
  }

  .mgdash-card-actions {
    width: 100%;
  }

  .mgdash-card-actions .mgdash-btn {
    flex: 1;
  }
}
`;